import { useEffect, useState } from 'react';
import { formatUTCDate } from '../../../../../util';
import { useClickAnchorContext } from '../../../ClickMenu';
import { useDatePickerContext } from '../datePicker.context';
import { DatePickerView } from '../types';

type useDatePickerFloatingMenuProps = {
  offsetYears?: number;
};

export const useDatePickerFloatingMenu = ({ offsetYears }: useDatePickerFloatingMenuProps) => {
  const {
    now,
    chosenDate,
    lookedYear,
    lookedMonth,
    currentView,
    setUserInput,
    setChosenDate,
    setCurrentView,
    setLookedMonth,
    setLookedYear,
  } = useDatePickerContext();
  const { setOpen } = useClickAnchorContext();
  const [yearOffset, setYearOffset] = useState(offsetYears ?? 0);
  const [noOfDays, setNoOfDays] = useState<number[]>([]);
  const [blankDays, setBlankDays] = useState<number[]>([]);

  const setDate = (day: number) => {
    // Build a UTC-anchored midnight Date so the resulting ISO string keeps the
    // calendar date the user actually clicked, independent of the host's TZ.
    const date = new Date(Date.UTC(lookedYear, lookedMonth, day));
    // Format from the UTC components so the displayed string never drifts a day
    // when the local timezone is east/west of UTC.
    setUserInput(formatUTCDate(date.toISOString(), 'MMM dd, yyyy'));
    setChosenDate(date);
    setOpen(false);
  };

  const getNoOfDays = () => {
    // Days in `lookedMonth` of `lookedYear` — use UTC math to avoid TZ off-by-one
    // around month boundaries.
    const daysInMonth = new Date(Date.UTC(lookedYear, lookedMonth + 1, 0)).getUTCDate();

    // find where to start calendar day of week
    const dayOfWeek = new Date(Date.UTC(lookedYear, lookedMonth, 1)).getUTCDay();
    const blankDaysArray = [];
    for (let i = 1; i <= dayOfWeek; i++) {
      blankDaysArray.push(i);
    }
    setBlankDays(blankDaysArray);

    const daysArray = [];
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }
    setNoOfDays(daysArray);
  };

  const traverseTime = (multiplier: -1 | 1) => {
    if (currentView === DatePickerView.DatePicker) {
      let month = lookedMonth + 1 * multiplier;
      if (month === -1) {
        month = 11;
        setLookedYear(lookedYear - 1);
      } else if (month === 12) {
        month = 0;
        setLookedYear(lookedYear + 1);
      }
      setLookedMonth(month);
    } else if (currentView === DatePickerView.YearPicker) {
      setYearOffset(yearOffset + 20 * multiplier);
    } else if (currentView === DatePickerView.MonthPicker) {
      setLookedYear(lookedYear + 1 * multiplier);
    }
  };

  useEffect(() => {
    const date = chosenDate ?? now;
    setLookedMonth(date.getUTCMonth());
    setLookedYear(date.getUTCFullYear());
  }, [chosenDate]);

  useEffect(() => setCurrentView(DatePickerView.DatePicker), []);

  useEffect(() => {
    getNoOfDays();
  }, [lookedMonth, lookedYear]);

  return {
    setDate,
    noOfDays,
    blankDays,
    traverseTime,
    yearOffset,
  };
};
